import { addMonths, format, parse, subMonths } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/ui/user-avatar'
import { UserLabel } from '@/components/ui/user-label'
import { PipelineSection, useMyPipeline } from '@/models/myPipeline'
import { type StaffUser, staffUserName, useStaffUsers } from '@/models/user'
import { AddToPipelineDialog } from './AddToPipelineDialog'
import { DealsTable } from './DealsTable'
import { PipelineStatCards } from './PipelineStatCards'
import { WarmAccountsTable } from './WarmAccountsTable'

const MONTH_PARAM_FORMAT = 'yyyy-MM'

function parseMonthParam(value: string | null): Date {
  if (!value) return new Date()
  const parsed = parse(value, MONTH_PARAM_FORMAT, new Date())
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

function ViewingAsSelect({
  value,
  staffUsers,
  onChange,
}: {
  value: string
  staffUsers: StaffUser[]
  onChange: (value: string) => void
}) {
  return (
    <Select value={value || 'me'} onValueChange={(next) => onChange(next === 'me' ? '' : next)}>
      <SelectTrigger className="w-56" aria-label="Viewing as">
        <SelectValue placeholder="Viewing as" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="me">Viewing as me</SelectItem>
        {staffUsers.map((user) => (
          <SelectItem key={user.id} value={user.id}>
            <UserLabel name={staffUserName(user)} src={user.avatar} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function MonthStepper({ month, onChange }: { month: Date; onChange: (next: Date) => void }) {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Previous month"
        onClick={() => onChange(subMonths(month, 1))}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <span className="min-w-36 text-center text-sm font-medium">{format(month, 'MMMM yyyy')}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Next month"
        onClick={() => onChange(addMonths(month, 1))}
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  )
}

export function MyPipeline() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { staffUsers } = useStaffUsers()

  const month = parseMonthParam(searchParams.get('month'))
  const monthParam = format(month, MONTH_PARAM_FORMAT)
  const repParam = searchParams.get('rep') ?? ''
  const previousMonth = subMonths(month, 1)
  const previousMonthParam = format(previousMonth, MONTH_PARAM_FORMAT)

  const { data, isLoading, isError } = useMyPipeline(monthParam, repParam || undefined)
  // The footer's one-line look-back; the same endpoint, a month earlier.
  const { data: previous } = useMyPipeline(previousMonthParam, repParam || undefined)

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  return (
    <div className="flex max-w-7xl flex-col gap-6 p-6">
      <Helmet>
        <title>My Pipeline</title>
      </Helmet>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {data && <UserAvatar name={data.rep.name} src={data.rep.avatar} />}
          <div>
            <h1 className="text-2xl font-semibold">My Pipeline</h1>
            <p className="text-muted-foreground text-sm">
              {data
                ? `${data.deals.length} deals this month · ${data.warmStats.total} warm accounts`
                : isError
                  ? 'Something went wrong'
                  : 'Loading your deals…'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthStepper
            month={month}
            onChange={(next) => setParam('month', format(next, MONTH_PARAM_FORMAT))}
          />
          <ViewingAsSelect
            value={repParam}
            staffUsers={staffUsers}
            onChange={(value) => setParam('rep', value)}
          />
          {data && (
            <AddToPipelineDialog
              repId={data.rep.id}
              section={PipelineSection.DEAL}
              month={`${monthParam}-01`}
              primary
            />
          )}
        </div>
      </div>

      {isLoading && <Skeleton className="h-28 w-full" />}

      {isError && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">This pipeline couldn’t be loaded.</p>
          {repParam && (
            <Button variant="outline" onClick={() => setParam('rep', '')}>
              Back to my pipeline
            </Button>
          )}
        </div>
      )}

      {data && (
        <>
          <PipelineStatCards stats={data.stats} />
          <DealsTable
            deals={data.deals}
            previousMonthStats={previous?.stats}
            previousMonthLabel={format(previousMonth, 'MMMM')}
            canEditDueDates={data.viewerIsRep}
          />
          <WarmAccountsTable
            accounts={data.warmAccounts}
            stats={data.warmStats}
            repId={data.rep.id}
            month={`${monthParam}-01`}
          />
        </>
      )}
    </div>
  )
}
